import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { persistReducer } from 'redux-persist'
import storage from 'redux-persist/lib/storage'

import { selectIndividualAction, signOutAction } from '../actions'

export type AssistantState = {
  isSideChatOpen: boolean
  activeThreadId?: string
  isChatFullscreen: boolean
  pendingMessage?: string
  // Id of a chat message to scroll to (set when the user clicks an errand action's
  // "Visa i chatten"). Consumed by the chat, then cleared.
  activeMessageId?: string
  // Id of the errand currently waiting for a proactive reaction (status change, upload or note)
  // to land, so the chat can show a loading indicator instead of going silent until the automated
  // message suddenly appears. Set by whichever page triggers the change, cleared by
  // `useErrandReactionRealtime` once the backend actually reacts.
  pendingErrandReactionId?: string
}

const initialState: AssistantState = {
  activeThreadId: undefined,
  isChatFullscreen: false,
  isSideChatOpen: false,
}

const key = 'assistant'

const AssistantSlice = createSlice({
  extraReducers: builder => {
    builder.addCase(selectIndividualAction, state => {
      state.isSideChatOpen = false
      state.isChatFullscreen = false
      state.activeThreadId = undefined
      state.activeMessageId = undefined
      state.pendingErrandReactionId = undefined
    })
    builder.addCase(signOutAction, state => {
      state.isSideChatOpen = initialState.isSideChatOpen
      state.activeThreadId = initialState.activeThreadId
    })
  },
  initialState,
  name: key,
  reducers: {
    setActiveThreadId(state, action: PayloadAction<string | undefined>) {
      state.activeThreadId = action.payload
    },
    setActiveMessageId(state, action: PayloadAction<string | undefined>) {
      state.activeMessageId = action.payload
    },
    setChatFullscreen(state, action: PayloadAction<boolean>) {
      state.isChatFullscreen = action.payload
    },
    setPendingErrandReaction(state, action: PayloadAction<string | undefined>) {
      state.pendingErrandReactionId = action.payload
    },
    setPendingMessage(state, action: PayloadAction<string | undefined>) {
      state.pendingMessage = action.payload
    },
    toggleSideChat(state, action: PayloadAction<boolean | undefined>) {
      if (action.payload !== undefined) {
        state.isSideChatOpen = action.payload
      } else {
        state.isSideChatOpen = !state.isSideChatOpen
      }

      if (!state.isSideChatOpen) {
        state.isChatFullscreen = false
      }
    },
  },
})

export const AssistantReducer = persistReducer(
  {
    key,
    storage,
    whitelist: ['isSideChatOpen'],
  },
  AssistantSlice.reducer,
)

export const AssistantActions = AssistantSlice.actions
